import { Box, Flex } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonDateRangeInput } from 'components/common/form/date/range';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { PitchPerfToggleThresholds } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/controls/toggle-thresholds';
import usePitchPerformanceStore from 'components/sections/admin-portal/machines/dialogs/pitch-performance/store/use-pitch-performance-store';
import {
  differenceInDays,
  endOfDay,
  endOfToday,
  parseISO,
  startOfDay,
  subDays,
} from 'date-fns';
import { t } from 'i18next';
import { LOCAL_DATEPICKER_FORMAT } from 'lib_ts/enums/datetime.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IPitchPerfFilters } from 'lib_ts/interfaces/data/i-pitch-performance';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { isNil } from 'lodash';
import { useMemo, useState } from 'react';

enum ShotType {
  Training = 'training',
  BP = 'bp',
  LowConfidence = 'low-confidence',
}

const SHOT_TYPE_OPTIONS: IOption[] = [
  {
    group: 'Shot Types',
    label: 'Training Shots',
    value: ShotType.Training,
  },
  {
    group: 'Shot Types',
    label: 'BP Shots',
    value: ShotType.BP,
  },
  {
    group: 'Confidence',
    label: 'Low Confidence Shots',
    value: ShotType.LowConfidence,
  },
];

export const PitchPerfControls = (props: { machine: IMachine }) => {
  const [maxDate] = useState(endOfToday());

  const filters = usePitchPerformanceStore((s) => s.filters);
  const loading = usePitchPerformanceStore((s) => s.loading);

  const { currPeriod } = filters;

  const [shotOptions] = useState<IOption[]>(
    [100, 250, 500, -1].map((n) => {
      const o: IOption = {
        label: n === -1 ? 'All shots' : `Last ${n} shots`,
        value: n.toString(),
      };

      return o;
    })
  );

  const ballTypeOptions = useMemo(
    () =>
      props.machine.ball_type_options.map((b) => {
        const o: IOption = {
          label: b,
          value: b,
        };

        return o;
      }),
    [props.machine.ball_type_options]
  );

  const selectedShotTypes = useMemo(() => {
    const output: ShotType[] = [];

    if (filters.includeLowConfidence) {
      output.push(ShotType.LowConfidence);
    }

    if (isNil(filters.training)) {
      output.push(ShotType.BP, ShotType.Training);
    } else if (filters.training) {
      output.push(ShotType.Training);
    } else {
      output.push(ShotType.BP);
    }

    return output;
  }, [filters.training, filters.includeLowConfidence]);

  return (
    <ErrorBoundary identifier="PitchPerfControls">
      <Flex direction="row" gap={RADIX.FLEX.GAP.FORM}>
        <Box flexGrow="1">
          <CommonDateRangeInput
            id="currPeriod"
            displayFormat={LOCAL_DATEPICKER_FORMAT}
            defaultStartValue={
              currPeriod.start ? parseISO(currPeriod.start) : undefined
            }
            defaultEndValue={
              currPeriod.end ? parseISO(currPeriod.end) : undefined
            }
            maxDate={maxDate}
            onChange={(values) => {
              if (!values || !values.start || !values.end) {
                NotifyHelper.warning({
                  message_md: t('common.check-inputs-msg'),
                });
                return;
              }

              const currStart = startOfDay(values.start);
              const currEnd = endOfDay(values.end);

              // determine the same-length period before the current one
              // e.g. if the current period is 1/15 - 1/19, the previous period will be 1/10 - 1/14
              const rangeLength = differenceInDays(currEnd, currStart) + 1;
              const prevEnd = subDays(currStart, 1);
              const prevStart = subDays(prevEnd, rangeLength - 1);

              usePitchPerformanceStore.getState().mergeFilters({
                currPeriod: {
                  start: currStart.toISOString(),
                  end: currEnd.toISOString(),
                },
                prevPeriod: {
                  start: prevStart.toISOString(),
                  end: prevEnd.toISOString(),
                },
              });
            }}
            disabled={loading}
            onChangeOnClose
          />
        </Box>

        <Box flexGrow="1">
          <CommonSelectInput
            id="shots"
            value={filters.shots.toString()}
            options={shotOptions}
            onNumericChange={(v) =>
              usePitchPerformanceStore.getState().mergeFilters({ shots: v })
            }
            disabled={loading}
            skipSort
          />
        </Box>

        <Box flexGrow="1">
          <CommonSelectInput
            id="ball_type"
            value={filters.ball_type}
            options={ballTypeOptions}
            onChange={(v) =>
              usePitchPerformanceStore
                .getState()
                .mergeFilters({ ball_type: v as BallType })
            }
            disabled={loading}
          />
        </Box>

        <Box flexGrow="1">
          <CommonSearchInput
            id="shot-types"
            values={selectedShotTypes}
            onChange={(values) => {
              const nextValue: Partial<IPitchPerfFilters> = {
                includeLowConfidence: values.includes(ShotType.LowConfidence),
              };

              if (
                values.includes(ShotType.Training) &&
                values.includes(ShotType.BP)
              ) {
                nextValue.training = null;
              } else if (values.includes(ShotType.Training)) {
                nextValue.training = true;
              } else if (values.includes(ShotType.BP)) {
                nextValue.training = false;
              }

              usePitchPerformanceStore.getState().mergeFilters(nextValue);
            }}
            options={SHOT_TYPE_OPTIONS}
            disabled={loading}
            maxDisplayItems={3}
            multiple
            hideSearch
            skipSort
          />
        </Box>

        <Box>
          <PitchPerfToggleThresholds />
        </Box>
      </Flex>
    </ErrorBoundary>
  );
};
